import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiService, type Podcast } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import type { MediaKitMetricsWindow } from "../api/podcasts";
import { normalizeMetricsWindow } from "../components/MediaKitMetricsWindowFields";
import { localizeShareUrl } from "../i18n/shareUrl";
import { useLocale } from "../i18n/LocaleProvider";

export const COMPANY_MEDIA_KITS_QUERY_KEY = "company-media-kits";

export type CompanyMediaKitRow = {
  podcast: Podcast;
  enabled: boolean;
  metricsWindow: MediaKitMetricsWindow;
  publicUrl: string | null;
};

export function mediaKitPublicUrl(
  companySlug: string | null | undefined,
  podcastSlug: string | null | undefined,
): string | null {
  if (!podcastSlug) return null;
  const url = companySlug
    ? `${window.location.origin}/${encodeURIComponent(companySlug)}/${encodeURIComponent(podcastSlug)}`
    : `${window.location.origin}/${encodeURIComponent(podcastSlug)}`;
  return localizeShareUrl(url);
}

/** Company podcasts with media kit on/off + metrics window, and a partial-update mutation. */
export function useCompanyMediaKits() {
  const { user } = useAuth();
  const { locale } = useLocale();
  const companyId = user?.companyId;
  const queryClient = useQueryClient();
  const queryKey = [COMPANY_MEDIA_KITS_QUERY_KEY, companyId];

  const podcastsQuery = useQuery({
    queryKey,
    queryFn: () => apiService.getCompanyPodcasts(companyId!),
    enabled: !!companyId,
    staleTime: 60 * 1000,
  });

  const { data: company } = useQuery({
    queryKey: ["settings-my-company", companyId],
    queryFn: () => apiService.getMyCompany(),
    enabled: !!companyId,
  });
  const companySlug = company?.slug ?? null;

  const rows: CompanyMediaKitRow[] = (podcastsQuery.data?.podcasts ?? []).map(
    (podcast) => ({
      podcast,
      enabled: podcast.mediaKitEnabled === true,
      metricsWindow: normalizeMetricsWindow(podcast.mediaKitMetricsWindow),
      publicUrl:
        locale === "en" || locale === "es"
          ? mediaKitPublicUrl(companySlug, podcast.slug)
          : null,
    }),
  );

  const patchMutation = useMutation({
    mutationFn: ({
      slug,
      patch,
    }: {
      podcastId: string;
      slug: string;
      patch: { enabled?: boolean; metricsWindow?: MediaKitMetricsWindow };
    }) => apiService.patchPodcastMediaKitSettings(slug, patch),
    onMutate: async ({ podcastId, patch }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<{ podcasts: Podcast[] }>(queryKey);
      queryClient.setQueryData<{ podcasts: Podcast[] }>(queryKey, (prev) =>
        prev
          ? {
              podcasts: prev.podcasts.map((p) =>
                String(p._id) === podcastId
                  ? {
                      ...p,
                      ...(patch.enabled !== undefined
                        ? { mediaKitEnabled: patch.enabled }
                        : {}),
                      ...(patch.metricsWindow
                        ? { mediaKitMetricsWindow: patch.metricsWindow }
                        : {}),
                    }
                  : p,
              ),
            }
          : prev,
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
    },
    onSettled: (_data, _err, { slug }) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["media-kit-settings", slug] });
    },
  });

  return {
    companyId,
    companySlug,
    rows,
    isLoading: podcastsQuery.isLoading,
    isError: podcastsQuery.isError,
    patchMutation,
  };
}
